/**
 * MonitoringV3.jsx — AudioSocket bot monitoring.
 *
 * Layout: left sidebar with CLIENTS ("All clients" first) + main panel with
 * tabs Stats / Info / Bots / VMs / Call Logs. Picking a client opens its Stats.
 * The filter row (SIPs or a group of SIPs, servers, templates) narrows every
 * tab. URL params: ?client=<id|all|unassigned>&tab=<key>&sip=<list>&search=<text>
 * (links from the Dashboard and the old /stats page).
 *
 * Reads from audiosocketBotsApi:
 *   listClients, listMachines, listServers, serverLxcs,
 *   listCallLogs (paginated + filtered), getCallLog (with events_json),
 *   machineRecordingUrl (audio player src)
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    Building2, Users, Server, Phone, BarChart3, Search, RefreshCw, X, Download, Play,
    Activity, Cpu, PhoneCall, PhoneIncoming, Headphones, ChevronLeft, ChevronRight, Tag, Square,
    Power, PowerOff, RotateCcw, Loader2, Undo2, Layers, FileText, FileCog, Trash2
} from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'
import HealthBadge from '../components/BotHealthBadge'
import UpdateCodeModal, { isOutdated } from '../components/BotCodeUpdate'
import BotEnvModal from '../components/BotEnvModal'
import MultiPicker from '../components/MultiPicker'
import CallStatsPanel from '../components/CallStatsPanel'
import './MonitoringV3.css'

const DISPOSITIONS = [
    { key: 'not-interested', label: 'Not Interested', color: '#f59e0b' },
    { key: 'no-answer', label: 'No Answer', color: '#3b82f6' },
    { key: 'answering-machine', label: 'Voicemail', color: '#6b7280' },
    { key: 'transfer-call', label: 'Transfer', color: '#22c55e' },
    { key: 'age-do-not-qualify', label: 'Age Do Not Qualify', color: '#ef4444' },
    { key: 'do-not-call', label: 'DNC', color: '#dc2626' },
    { key: 'busy', label: 'Busy', color: '#a78bfa' },
    { key: 'call-back', label: 'Call Back', color: '#06b6d4' },
    // Synthetic keys from /call-logs (disposition_key): in-progress = no end
    // report yet and started <10 min ago; unknown = no end report after that.
    { key: 'in-progress', label: 'In Progress', color: '#38bdf8' },
    { key: 'unknown', label: 'Unknown', color: '#94a3b8' }
]
const DISPO_BY_KEY = DISPOSITIONS.reduce((m, d) => { m[d.key] = d; return m }, {})
const DISPO_LABEL = (k) => DISPO_BY_KEY[k]?.label || k || 'Unknown'
const DISPO_COLOR = (k) => DISPO_BY_KEY[k]?.color || '#94a3b8'

function ymd(d) {
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}
// Business day runs 6 PM local → 6 AM local the next morning. A business day
// is identified by the calendar date it STARTS on (so "Thursday's shift" =
// Thu 18:00 → Fri 06:00).
const DAY_START_HOUR = 18  // 6 PM
const DAY_LENGTH_MS = 12 * 3600 * 1000
function businessDayStart(refDate) {
    // Local Date at refDate's date @ 18:00
    return new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), DAY_START_HOUR, 0, 0, 0)
}
// Given any moment, return the Date object for the START of the current business day.
function currentBusinessDayStart(now = new Date()) {
    if (now.getHours() >= DAY_START_HOUR) return businessDayStart(now)
    const y = new Date(now); y.setDate(y.getDate() - 1)
    return businessDayStart(y)
}
function rangeFor(preset) {
    const start = currentBusinessDayStart()
    const end = new Date(start.getTime() + DAY_LENGTH_MS)
    if (preset === 'today') return { from: start.toISOString(), to: end.toISOString() }
    if (preset === 'yesterday') {
        const ys = new Date(start); ys.setDate(ys.getDate() - 1)
        const ye = new Date(end); ye.setDate(ye.getDate() - 1)
        return { from: ys.toISOString(), to: ye.toISOString() }
    }
    if (preset === '7days') {
        const ws = new Date(start); ws.setDate(ws.getDate() - 6)  // last 7 business days inclusive
        return { from: ws.toISOString(), to: end.toISOString() }
    }
    return { from: '', to: '' }
}
// Custom date inputs (YYYY-MM-DD) → business-day window in UTC ISO.
// "from" date = start at that date's 18:00; "to" date = end at the morning AFTER that date (i.e. that date's 06:00 next day).
function customRangeToBusinessDay(fromYmd, toYmd) {
    if (!fromYmd && !toYmd) return { from: '', to: '' }
    const out = {}
    if (fromYmd) {
        const [y, m, d] = fromYmd.split('-').map(Number)
        out.from = new Date(y, (m || 1) - 1, d || 1, DAY_START_HOUR, 0, 0, 0).toISOString()
    }
    if (toYmd) {
        const [y, m, d] = toYmd.split('-').map(Number)
        const s = new Date(y, (m || 1) - 1, d || 1, DAY_START_HOUR, 0, 0, 0)
        out.to = new Date(s.getTime() + DAY_LENGTH_MS).toISOString()
    }
    return out
}
function fmtDateTime(s) {
    if (!s) return '—'
    // Server may return ISO ("2026-05-22T01:07:21.000Z") OR MySQL space-format
    // ("2026-05-22 01:07:21"). Only append 'Z' for the latter; appending it to
    // an already-zoned ISO string produces "...ZZ" → Invalid Date → raw fallback.
    const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(s)
    const d = new Date(hasTz ? s : s.replace(' ', 'T') + 'Z')
    if (isNaN(d.getTime())) return s
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}
function fmtDuration(sec) {
    if (sec == null) return '—'
    const m = Math.floor(sec / 60); const s = sec % 60
    return `${m}:${String(s).padStart(2, '0')}`
}

// ============================================================
// Inline Recording Player — single-click to play, no popup
// ============================================================
function InlineRecordingPlayer({ machineId, filename, durationSeconds }) {
    const [isPlaying, setIsPlaying] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const audioRef = useRef(null)

    if (!machineId || !filename) return <span style={{ opacity: 0.5 }}>—</span>

    const src = audiosocketBotsApi.machineRecordingUrl(machineId, filename)

    const togglePlay = (e) => {
        e.stopPropagation()
        e.preventDefault()
        const audio = audioRef.current
        if (!audio) return
        if (isPlaying) {
            audio.pause()
            setIsPlaying(false)
        } else {
            document.querySelectorAll('audio').forEach(a => { if (a !== audio) a.pause() })
            setIsLoading(true)
            audio.play().then(() => {
                setIsPlaying(true)
                setIsLoading(false)
            }).catch(err => {
                console.error('Playback error:', err)
                setIsLoading(false)
                setIsPlaying(false)
            })
        }
    }

    return (
        <button
            type="button"
            onClick={togglePlay}
            onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault() }}
            title={isPlaying ? 'Pause' : 'Play recording'}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 4,
                background: isPlaying ? '#3b82f6' : 'rgba(59,130,246,0.15)',
                color: isPlaying ? 'white' : '#3b82f6',
                fontSize: 11, border: 'none', cursor: 'pointer',
                opacity: isLoading ? 0.7 : 1
            }}
        >
            <audio ref={audioRef} src={src} preload="none"
                onEnded={() => setIsPlaying(false)}
                onPause={() => { setIsPlaying(false); setIsLoading(false) }}
                onError={() => { setIsPlaying(false); setIsLoading(false) }}
            />
            {isPlaying ? <Square size={10} /> : <Play size={10} />}
            {fmtDuration(durationSeconds)}
        </button>
    )
}

// ============================================================
// ROOT
// ============================================================

const ALL_ID = '__all__'
const UNASSIGNED_ID = '__unassigned__'
const NO_FILTERS = { sips: [], servers: [], templates: [] }

export default function MonitoringV3() {
    const [urlParams, setUrlParams] = useSearchParams()
    const [clients, setClients] = useState([])
    const [machines, setMachines] = useState([])
    const [currentBotVersion, setCurrentBotVersion] = useState(null)
    const [servers, setServers] = useState([])
    const [selectedClientId, setSelectedClientId] = useState(() => {
        const c = urlParams.get('client')
        if (!c || c === 'all') return ALL_ID
        if (c === 'unassigned') return UNASSIGNED_ID
        return Number.isFinite(+c) ? +c : ALL_ID
    })
    const [search, setSearch] = useState('')
    const [tab, setTab] = useState(() => urlParams.get('tab') || 'stats')
    const [filters, setFilters] = useState(() => ({ ...NO_FILTERS, sips: (urlParams.get('sip') || '').split(',').filter(Boolean) }))
    const [initialLogSearch] = useState(() => urlParams.get('search') || '')
    const [datePreset, setDatePreset] = useState('today')
    const [customFrom, setCustomFrom] = useState('')
    const [customTo, setCustomTo] = useState('')
    const [loading, setLoading] = useState(true)

    async function loadAll() {
        setLoading(true)
        try {
            const [c, m, s] = await Promise.all([
                audiosocketBotsApi.listClients(),
                audiosocketBotsApi.listMachines(),
                audiosocketBotsApi.listServers()
            ])
            setClients(c.data.clients || [])
            setMachines(m.data.machines || [])
            setCurrentBotVersion(m.data.current_bot_version || null)
            setServers(s.data.servers || [])
        } catch (e) {
            console.error('load monitoring failed:', e)
        } finally { setLoading(false) }
    }
    useEffect(() => { loadAll() }, [])

    // Virtual "Unassigned" pseudo-client — represents bots whose templates have client_id=NULL.
    // Sidebar shows it when at least one such machine exists.
    const unassignedMachineCount = useMemo(
        () => machines.filter(m => m.client_id == null).length,
        [machines]
    )
    // Only show clients that actually have bots. A client with 0 machines has
    // nothing to monitor, so it's hidden here — removing a client's last bot makes
    // it disappear from this list automatically (no need to also delete the client).
    const displayClients = useMemo(() => {
        const withBots = new Set(machines.map(m => m.client_id).filter(id => id != null))
        const arr = [{ id: ALL_ID, client_name: 'All clients', _virtual: true, _all: true },
            ...clients.filter(c => withBots.has(c.id))]
        if (unassignedMachineCount > 0) {
            arr.push({ id: UNASSIGNED_ID, client_name: 'Unassigned', _virtual: true })
        }
        return arr
    }, [clients, machines, unassignedMachineCount])

    const selectedClient = useMemo(
        () => displayClients.find(c => c.id === selectedClientId),
        [displayClients, selectedClientId]
    )
    const clientMachines = useMemo(() => {
        if (!selectedClientId) return []
        if (selectedClientId === ALL_ID) return machines
        if (selectedClientId === UNASSIGNED_ID) return machines.filter(m => m.client_id == null)
        return machines.filter(m => m.client_id === selectedClientId)
    }, [machines, selectedClientId])

    // Bots left after the SIP / server / template filters — what every tab shows.
    const scopedMachines = useMemo(() => {
        const sips = new Set(filters.sips), srv = new Set(filters.servers.map(String)), tpl = new Set(filters.templates.map(String))
        return clientMachines.filter(m =>
            (!sips.size || sips.has(String(m.sip_user))) &&
            (!srv.size || srv.has(String(m.server_id))) &&
            (!tpl.size || tpl.has(String(m.template_id))))
    }, [clientMachines, filters])

    // The same scope as API params for call logs / stats.
    const scopeParams = useMemo(() => {
        const p = {}
        if (selectedClientId === UNASSIGNED_ID) p.unassigned = 1
        else if (selectedClientId && selectedClientId !== ALL_ID) p.client_id = selectedClientId
        if (filters.sips.length) p.sip_user = filters.sips.join(',')
        if (filters.servers.length) p.server_id = filters.servers.join(',')
        if (filters.templates.length) p.template_id = filters.templates.join(',')
        return p
    }, [selectedClientId, filters])

    // Picking a client opens its stats with the filters cleared.
    function pickClient(id) {
        if (id === selectedClientId) return
        setSelectedClientId(id)
        setFilters(NO_FILTERS)
        setTab('stats')
    }

    // Keep the URL in step so the view can be linked / reloaded.
    useEffect(() => {
        const c = selectedClientId === ALL_ID ? 'all' : selectedClientId === UNASSIGNED_ID ? 'unassigned' : selectedClientId
        const next = new URLSearchParams({ client: String(c), tab })
        if (filters.sips.length) next.set('sip', filters.sips.join(','))
        setUrlParams(next, { replace: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClientId, tab, filters.sips])

    // Info / VMs are per client — "All clients" falls back to Stats.
    useEffect(() => {
        if (selectedClientId === ALL_ID && (tab === 'info' || tab === 'vms')) setTab('stats')
    }, [selectedClientId, tab])

    // A client from the URL that has no bots (or doesn't exist) → All clients.
    useEffect(() => {
        if (!loading && selectedClientId !== ALL_ID && !displayClients.some(c => c.id === selectedClientId)) setSelectedClientId(ALL_ID)
    }, [loading, displayClients, selectedClientId])

    const range = useMemo(() => {
        if (datePreset === 'custom') return customRangeToBusinessDay(customFrom, customTo)
        return rangeFor(datePreset)
    }, [datePreset, customFrom, customTo])

    const filteredClients = useMemo(() => {
        if (!search) return displayClients
        const q = search.toLowerCase()
        return displayClients.filter(c =>
            (c.client_name || c.name || '').toLowerCase().includes(q)
        )
    }, [displayClients, search])

    return (
        <div className="monitoring-page-v3">
            <div className="monitoring-layout">
                {/* ============ LEFT — CLIENTS ============ */}
                <aside className="dialer-sidebar-v2">
                    <div className="sidebar-header-v2">
                        <div className="sidebar-title-row">
                            <h3>
                                <Building2 size={16} />
                                Clients
                                <span className="count">{displayClients.filter(c => !c._all).length}</span>
                            </h3>
                            <button className="btn btn-icon btn-sm" onClick={loadAll} disabled={loading}>
                                <RefreshCw size={14} className={loading ? 'spin' : ''} />
                            </button>
                        </div>
                        <div className="sidebar-search">
                            <Search size={14} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Filter clients..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="dialer-list-v2">
                        {loading ? (
                            <div style={{ padding: 16, fontSize: 12, opacity: 0.6 }}>loading…</div>
                        ) : filteredClients.length === 0 ? (
                            <div style={{ padding: 16, fontSize: 12, opacity: 0.6 }}>
                                {clients.length === 0 ? 'No clients yet.' : 'No matches.'}
                            </div>
                        ) : filteredClients.map(c => {
                            const botCount = c.id === ALL_ID ? machines.length
                                : c.id === UNASSIGNED_ID ? unassignedMachineCount
                                : machines.filter(m => m.client_id === c.id).length
                            const isSelected = c.id === selectedClientId
                            const isVirtual = c._virtual
                            return (
                                <div key={c.id}
                                    className={`dialer-item-v2 ${isSelected ? 'selected' : ''}`}
                                    onClick={() => pickClient(c.id)}>
                                    <div className="dialer-item-content">
                                        <div className="dialer-item-status">
                                            {c._all
                                                ? <Layers size={14} className="tw-text-sky-400" />
                                                : <span className={`dialer-status-dot ${isVirtual ? 'idle' : 'online'}`}></span>}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="dialer-name" style={{ fontSize: 13, fontWeight: 600, fontStyle: isVirtual && !c._all ? 'italic' : 'normal' }}>
                                                {c.client_name || c.name || `Client #${c.id}`}
                                            </div>
                                            <div className="dialer-meta" style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                                                {botCount} bot{botCount === 1 ? '' : 's'}{c._all ? ` · ${displayClients.length - 1} clients` : isVirtual ? ' · no client_id on template' : ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </aside>

                {/* ============ RIGHT — CONTENT ============ */}
                <main className="dialer-content">
                    {!selectedClient ? (
                        <div className="loading-state">
                            <Building2 size={48} style={{ opacity: 0.3 }} />
                            <p>Select a client from the sidebar to view their bots and call logs.</p>
                        </div>
                    ) : (
                        <>
                            <ClientHeader
                                client={selectedClient}
                                machinesCount={scopedMachines.length}
                                tab={tab}
                                onTabChange={setTab}
                                datePreset={datePreset}
                                onDatePreset={setDatePreset}
                                customFrom={customFrom}
                                customTo={customTo}
                                onCustomChange={(f, t) => { setCustomFrom(f); setCustomTo(t) }}
                                onRefresh={loadAll}
                            />

                            <ScopeFilters machines={clientMachines} servers={servers} filters={filters} onChange={setFilters}
                                shown={scopedMachines.length} />

                            {tab === 'info' && <InfoTab client={selectedClient} machines={scopedMachines} servers={servers} />}
                            {tab === 'bots' && <BotsTab currentVersion={currentBotVersion} client={selectedClient} machines={scopedMachines} onChanged={loadAll} />}
                            {tab === 'vms' && <VMsTab client={selectedClient} machines={scopedMachines} servers={servers} />}
                            {tab === 'call_logs' && <CallLogsTab client={selectedClient} machines={scopedMachines} range={range} scopeParams={scopeParams} initialSearch={initialLogSearch} />}
                            {tab === 'stats' && (
                                <div className="dialer-info-tab">
                                    <CallStatsPanel
                                        params={{ ...scopeParams, ...(range.from ? { from: range.from } : {}), ...(range.to ? { to: range.to } : {}) }}
                                        machines={scopedMachines}
                                        showClients={selectedClientId === ALL_ID}
                                        onPickClient={(id) => pickClient(id == null ? UNASSIGNED_ID : id)}
                                        onFilterSips={(sips) => setFilters(f => ({ ...f, sips }))} />
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    )
}

// ============================================================
// HEADER
// ============================================================

function ClientHeader({ client, machinesCount, tab, onTabChange, datePreset, onDatePreset, customFrom, customTo, onCustomChange, onRefresh }) {
    const all = client.id === ALL_ID
    const tabs = [
        { key: 'stats', label: 'Stats', icon: BarChart3 },
        !all && { key: 'info', label: 'Info', icon: Activity },
        { key: 'bots', label: `Bots (${machinesCount})`, icon: Cpu },
        !all && { key: 'vms', label: 'VMs', icon: Server },
        { key: 'call_logs', label: 'Call Logs', icon: Phone }
    ].filter(Boolean)
    const datePills = [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: '7days', label: '7 Days' },
        { key: 'custom', label: 'Custom' }
    ]

    return (
        <div className="content-header" style={{ flexWrap: 'wrap', gap: 12 }}>
            <div className="content-header-left" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 17, display: 'flex', alignItems: 'center', gap: 10 }}>
                    {client.client_name || client.name || `Client #${client.id}`}
                    {!client._virtual && <span style={{
                        fontSize: 11, fontWeight: 600,
                        padding: '3px 10px', borderRadius: 12,
                        background: 'rgba(34,197,94,0.15)', color: '#22c55e'
                    }}>Active</span>}
                </h2>

                <div className="content-tabs" style={{ borderBottom: 'none' }}>
                    {tabs.map(t => {
                        const I = t.icon
                        return (
                            <button key={t.key}
                                className={`content-tab ${tab === t.key ? 'active' : ''}`}
                                onClick={() => onTabChange(t.key)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <I size={14} /> {t.label}
                            </button>
                        )
                    })}
                </div>
            </div>

            <div className="content-header-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="date-filter-pills">
                    {datePills.map(p => (
                        <button key={p.key}
                            className={`date-pill ${datePreset === p.key ? 'active' : ''}`}
                            onClick={() => onDatePreset(p.key)}>
                            {p.label}
                        </button>
                    ))}
                </div>
                {datePreset === 'custom' && (
                    <div className="date-custom-inputs" style={{ display: 'flex', gap: 4 }}>
                        <input type="date" value={customFrom} onChange={e => onCustomChange(e.target.value, customTo)} />
                        <input type="date" value={customTo} onChange={e => onCustomChange(customFrom, e.target.value)} />
                    </div>
                )}
                <button className="btn btn-icon btn-sm" onClick={onRefresh} title="Refresh">
                    <RefreshCw size={14} />
                </button>
            </div>
        </div>
    )
}

// ============================================================
// INFO TAB
// ============================================================

function InfoTab({ client, machines, servers }) {
    const serversForClient = useMemo(() => {
        const ids = new Set(machines.map(m => m.server_id).filter(Boolean))
        return servers.filter(s => ids.has(s.id))
    }, [machines, servers])

    const activeBots = machines.filter(m => m.status === 'active').length
    const avatarSet = new Set(machines.map(m => m.avatar_name).filter(Boolean))
    const voiceSetSet = new Set(machines.map(m => m.voice_set_name).filter(Boolean))
    const templateSet = new Set(machines.map(m => m.template_name).filter(Boolean))

    return (
        <div className="dialer-info-tab">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                <StatCard icon={Cpu} label="Bots" value={machines.length} />
                <StatCard icon={Activity} label="Active" value={activeBots} />
                <StatCard icon={Server} label="Servers" value={serversForClient.length} />
                <StatCard icon={Users} label="Avatars" value={avatarSet.size} />
            </div>

            <div className="dialer-details-card">
                <div className="details-header" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Building2 size={16} /> <strong>{client.client_name || client.name || `Client #${client.id}`}</strong>
                </div>
                <div className="details-grid">
                    <Detail label="Client ID" value={client.id} mono />
                    <Detail label="Bots" value={`${machines.length} (${activeBots} active)`} />
                    <Detail label="Templates" value={Array.from(templateSet).join(', ') || '—'} />
                    <Detail label="Avatars" value={Array.from(avatarSet).join(', ') || '—'} />
                    <Detail label="Voice Sets" value={Array.from(voiceSetSet).join(', ') || '—'} />
                    <Detail label="Servers" value={serversForClient.map(s => `${s.name} (${s.host_ip})`).join(', ') || '—'} />
                </div>
            </div>
        </div>
    )
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div style={{
            padding: '14px 16px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: 10
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                <Icon size={14} /> {label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
        </div>
    )
}
function Detail({ label, value, mono }) {
    return (
        <div className="detail-item" style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 0' }}>
            <span className="detail-label" style={{ fontSize: 11, opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
            <span className="detail-value" style={mono ? { fontFamily: 'monospace' } : {}}>{value}</span>
        </div>
    )
}

// ============================================================
// BOTS TAB
// ============================================================

// Icon-only action button for a bot row; the label shows on hover.
function RowAction({ icon: Icon, label, color, onClick, busy }) {
    return (
        <button onClick={onClick} disabled={busy} title={label} aria-label={label}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, marginRight: 4, padding: 0, borderRadius: 6,
                border: `1px solid ${color}55`, background: `${color}14`, color,
                cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1
            }}>
            <Icon size={13} />
        </button>
    )
}

function BotsTab({ client, machines, onChanged, currentVersion }) {
    const [selected, setSelected] = useState(() => new Set())
    const [showModal, setShowModal] = useState(false)
    const [removeTemplate, setRemoveTemplate] = useState(false)
    const [busy, setBusy] = useState(false)
    const [result, setResult] = useState(null)

    // Drop selections for bots no longer in the list (after a removal/reload).
    useEffect(() => {
        setSelected(prev => {
            const ids = new Set(machines.map(m => m.id))
            const next = new Set([...prev].filter(id => ids.has(id)))
            return next.size === prev.size ? prev : next
        })
    }, [machines])

    const allSelected = machines.length > 0 && selected.size === machines.length
    const toggle = (id) => setSelected(prev => {
        const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n
    })
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(machines.map(m => m.id)))

    const selectedMachines = machines.filter(m => selected.has(m.id))

    async function doRemove() {
        setBusy(true); setResult(null)
        try {
            const r = await audiosocketBotsApi.bulkRemoveMachines([...selected], removeTemplate)
            const d = r?.data || r
            setResult(d)
            if (d?.success) {
                setSelected(new Set())
                if (onChanged) await onChanged()
            }
        } catch (e) {
            setResult({ success: false, error: e?.response?.data?.error || e.message })
        } finally {
            setBusy(false)
        }
    }

    function closeModal() {
        if (busy) return
        setShowModal(false); setRemoveTemplate(false); setResult(null)
    }

    const [powerBusy, setPowerBusy] = useState(null) // 'stop' | 'start' | 'restart'
    async function doPower(action, ids = [...selected]) {
        const labels = { stop: 'Turn OFF', start: 'Turn ON', restart: 'Restart' }
        const n = ids.length
        if (!n) return
        let msg = `${labels[action]} ${n} bot${n === 1 ? '' : 's'}?`
        if (action === 'stop') msg += '\n\nThe containers stop — these bots will NOT take calls until turned on.'
        if (action === 'start') msg += '\n\nStarted 5 per server, then a 10s wait, then the next 5.'
        if (action === 'restart') msg += '\n\nRestarts the bot service to reload avatar changes. Done 5 per server, 10s between batches.'
        if (!window.confirm(msg)) return
        setPowerBusy(action)
        try {
            const r = await audiosocketBotsApi.bulkPowerMachines(ids, action)
            const d = r?.data || r
            const failed = (d.results || []).filter(x => !x.ok)
            alert(`${labels[action]}: ${d.ok_count}/${d.count} ok`
                + (failed.length ? `\n\nFailed:\n${failed.map(f => `SIP ${f.sip_user}: ${(f.error || f.out || '').slice(0, 60)}`).join('\n')}` : ''))
            if (onChanged) await onChanged()
        } catch (e) {
            alert('Failed: ' + (e?.response?.data?.error || e.message))
        } finally { setPowerBusy(null) }
    }
    const powerDisabled = selected.size === 0 || !!powerBusy
    const [updatingBots, setUpdatingBots] = useState(null)   // { mode: 'update'|'revert', bots } open in the code dialog
    const [envMachine, setEnvMachine] = useState(null)       // bot whose .env editor is open
    const outdated = machines.filter(m => isOutdated(m, currentVersion))

    return (
        <div className="dialer-info-tab">
            {updatingBots && (
                <UpdateCodeModal mode={updatingBots.mode} bots={updatingBots.bots} currentVersion={currentVersion}
                    onClose={() => setUpdatingBots(null)}
                    onDone={() => { setSelected(new Set()); onChanged && onChanged() }} />
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {machines.length} bot{machines.length === 1 ? '' : 's'} assigned to {client.client_name || client.name}
                    {selected.size > 0 && <span style={{ marginLeft: 8, color: '#f59e0b', fontWeight: 600 }}>· {selected.size} selected</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {(() => {
                        const pbtn = (action, Icon, label, color) => (
                            <button disabled={powerDisabled} onClick={() => doPower(action)}
                                title={label + (action === 'stop' ? ' (immediate)' : ' — 5 per server, 10s apart')}
                                style={{
                                    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    border: '1px solid ' + (powerDisabled ? 'var(--border-color)' : color),
                                    background: powerDisabled ? 'transparent' : color + '20',
                                    color: powerDisabled ? 'var(--text-secondary, #94a3b8)' : color,
                                    cursor: powerDisabled ? 'not-allowed' : 'pointer'
                                }}>
                                {powerBusy === action ? <Loader2 size={13} className="spin" /> : <Icon size={13} />} {label}
                            </button>
                        )
                        return <>
                            {currentVersion && outdated.length > 0 && (
                                <button onClick={() => setSelected(new Set(outdated.map(m => m.id)))}
                                    title={`Select the ${outdated.length} bots not on ${currentVersion}`}
                                    className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-violet-400 hover:tw-underline">
                                    Select outdated ({outdated.length})
                                </button>
                            )}
                            <button disabled={powerDisabled}
                                onClick={() => setUpdatingBots({ mode: 'update', bots: selectedMachines })}
                                title={`Install the latest bot code${currentVersion ? ` (${currentVersion})` : ''} — waits for a gap between calls`}
                                style={{
                                    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    border: '1px solid ' + (powerDisabled ? 'var(--border-color)' : '#8b5cf6'),
                                    background: powerDisabled ? 'transparent' : '#8b5cf620',
                                    color: powerDisabled ? 'var(--text-secondary, #94a3b8)' : '#8b5cf6',
                                    cursor: powerDisabled ? 'not-allowed' : 'pointer'
                                }}>
                                <Download size={13} /> Update code
                            </button>
                            <button disabled={powerDisabled}
                                onClick={() => setUpdatingBots({ mode: 'revert', bots: selectedMachines })}
                                title="Put back the code the last update replaced — waits for a gap between calls"
                                style={{
                                    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    border: '1px solid ' + (powerDisabled ? 'var(--border-color)' : '#94a3b8'),
                                    background: 'transparent',
                                    color: powerDisabled ? 'var(--text-secondary, #94a3b8)' : 'var(--text-primary)',
                                    cursor: powerDisabled ? 'not-allowed' : 'pointer'
                                }}>
                                <Undo2 size={13} /> Revert
                            </button>
                            {pbtn('restart', RotateCcw, 'Restart', '#3b82f6')}
                            {pbtn('start', Power, 'Turn on', '#22c55e')}
                            {pbtn('stop', PowerOff, 'Turn off', '#f59e0b')}
                        </>
                    })()}
                    <button
                        disabled={selected.size === 0}
                        onClick={() => setShowModal(true)}
                        style={{
                            fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 8,
                            border: '1px solid ' + (selected.size ? '#ef4444' : 'var(--border-color)'),
                            background: selected.size ? 'rgba(239,68,68,0.12)' : 'transparent',
                            color: selected.size ? '#ef4444' : 'var(--text-secondary, #94a3b8)',
                            cursor: selected.size ? 'pointer' : 'not-allowed'
                        }}>
                        🗑 Remove selected{selected.size ? ` (${selected.size})` : ''}
                    </button>
                </div>
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10, overflow: 'auto' }}>
                {machines.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', opacity: 0.6 }}>
                        No bots for this client yet. Create a template with this client_id and clone bots.
                    </div>
                ) : (
                    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', opacity: 0.7, borderBottom: '1px solid var(--border-color)' }}>
                                <th style={{ padding: '10px 12px', width: 34 }}>
                                    <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all" />
                                </th>
                                <th style={{ padding: '10px 12px' }}>SIP</th>
                                <th style={{ padding: '10px 12px' }}>Template</th>
                                <th style={{ padding: '10px 12px' }}>Avatar</th>
                                <th style={{ padding: '10px 12px' }}>Voice Set</th>
                                <th style={{ padding: '10px 12px' }}>Server</th>
                                <th style={{ padding: '10px 12px' }}>LXC</th>
                                <th style={{ padding: '10px 12px' }}>IP</th>
                                <th style={{ padding: '10px 12px' }}>Status</th>
                                <th style={{ padding: '10px 12px' }}>Health</th>
                                <th style={{ padding: '10px 12px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {machines.map(m => {
                                const isSel = selected.has(m.id)
                                return (
                                    <tr key={m.id} style={{ borderTop: '1px solid var(--border-color)', background: isSel ? 'rgba(239,68,68,0.06)' : 'transparent' }}>
                                        <td style={{ padding: '8px 12px' }}>
                                            <input type="checkbox" checked={isSel} onChange={() => toggle(m.id)} />
                                        </td>
                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600 }}>{m.sip_user}</td>
                                        <td style={{ padding: '8px 12px' }}>{m.template_name || '—'}</td>
                                        <td style={{ padding: '8px 12px' }}>{m.avatar_name || '—'}</td>
                                        <td style={{ padding: '8px 12px' }}>{m.voice_set_name || '—'}</td>
                                        <td style={{ padding: '8px 12px' }}>{m.server_name || `#${m.server_id}`}</td>
                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{m.lxc_id}</td>
                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{m.lxc_ip || '—'}</td>
                                        <td style={{ padding: '8px 12px' }}>
                                            <span style={{
                                                fontSize: 11, fontWeight: 500,
                                                padding: '2px 8px', borderRadius: 10,
                                                background: m.status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(156,163,175,0.15)',
                                                color: m.status === 'active' ? '#22c55e' : '#9ca3af'
                                            }}>{m.status || 'unknown'}</span>
                                        </td>
                                        <td style={{ padding: '8px 12px' }}><HealthBadge health={m.health} version={m.version} /></td>
                                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                            <RowAction icon={Download} label="Update code" color="#8b5cf6"
                                                onClick={() => setUpdatingBots({ mode: 'update', bots: [m] })} />
                                            <RowAction icon={Undo2} label="Revert code" color="#94a3b8"
                                                onClick={() => setUpdatingBots({ mode: 'revert', bots: [m] })} />
                                            <RowAction icon={RotateCcw} label="Restart" color="#3b82f6" busy={!!powerBusy}
                                                onClick={() => doPower('restart', [m.id])} />
                                            {m.status === 'active'
                                                ? <RowAction icon={PowerOff} label="Turn off" color="#f59e0b" busy={!!powerBusy}
                                                    onClick={() => doPower('stop', [m.id])} />
                                                : <RowAction icon={Power} label="Turn on" color="#22c55e" busy={!!powerBusy}
                                                    onClick={() => doPower('start', [m.id])} />}
                                            <RowAction icon={FileCog} label="Settings (.env)" color="#3b82f6"
                                                onClick={() => setEnvMachine(m)} />
                                            <RowAction icon={Trash2} label="Remove" color="#ef4444"
                                                onClick={() => { setSelected(new Set([m.id])); setShowModal(true) }} />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {envMachine && <BotEnvModal machine={envMachine} onClose={() => setEnvMachine(null)} />}

            {showModal && (
                <div onClick={closeModal} style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        background: 'var(--bg-card, #1e293b)', border: '1px solid var(--border-color)',
                        borderRadius: 12, padding: 22, width: 460, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.4)'
                    }}>
                        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#ef4444' }}>
                            Remove {selectedMachines.length} bot{selectedMachines.length === 1 ? '' : 's'}?
                        </div>

                        {!result && <>
                            <div style={{ fontSize: 12.5, opacity: 0.85, marginBottom: 10 }}>
                                This is a <b>full</b> removal. For each selected bot it will:
                            </div>
                            <ul style={{ fontSize: 12.5, lineHeight: 1.7, margin: '0 0 12px 18px', opacity: 0.9 }}>
                                <li>Destroy the LXC container on its server</li>
                                <li>Remove its registration from the central Asterisk</li>
                                <li>Delete its license</li>
                                <li>Delete its machine + SIP-assignment records</li>
                            </ul>
                            <div style={{
                                fontSize: 11.5, padding: '7px 10px', borderRadius: 8, marginBottom: 12,
                                background: 'rgba(34,197,94,0.10)', color: '#86efac', border: '1px solid rgba(34,197,94,0.25)'
                            }}>
                                ✓ Avatars, keywords and voice sets are shared — they are <b>not</b> touched.
                            </div>
                            <div style={{
                                maxHeight: 110, overflow: 'auto', fontSize: 12, fontFamily: 'monospace',
                                background: 'var(--bg-input, rgba(0,0,0,0.2))', borderRadius: 8, padding: '8px 10px', marginBottom: 14
                            }}>
                                {selectedMachines.map(m => (
                                    <div key={m.id}>SIP {m.sip_user} · LXC {m.lxc_id} · {m.server_name || `srv#${m.server_id}`}</div>
                                ))}
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginBottom: 16, cursor: 'pointer' }}>
                                <input type="checkbox" checked={removeTemplate} onChange={e => setRemoveTemplate(e.target.checked)} />
                                Also remove the template &amp; client (only if no bots remain) — clears it from this list
                            </label>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button disabled={busy} onClick={closeModal} style={{
                                    fontSize: 13, padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                                    border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary, #94a3b8)'
                                }}>Cancel</button>
                                <button disabled={busy} onClick={doRemove} style={{
                                    fontSize: 13, fontWeight: 700, padding: '8px 18px', borderRadius: 8,
                                    border: 'none', background: '#ef4444', color: '#fff', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1
                                }}>{busy ? 'Removing…' : `Remove ${selectedMachines.length}`}</button>
                            </div>
                        </>}

                        {result && (
                            <div>
                                <div style={{
                                    fontSize: 13, fontWeight: 600, marginBottom: 10,
                                    color: result.success ? '#22c55e' : '#ef4444'
                                }}>
                                    {result.success
                                        ? `✓ Removed ${result.removed_count} bot${result.removed_count === 1 ? '' : 's'}.`
                                        : `✗ ${result.error || 'Removal failed'}`}
                                </div>
                                {result.success && (result.removed_templates?.length > 0 || result.removed_clients?.length > 0) && (
                                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
                                        Also removed template{result.removed_templates?.length === 1 ? '' : 's'} {result.removed_templates?.join(', ')}
                                        {result.removed_clients?.length > 0 && ` and client ${result.removed_clients.join(', ')}`}.
                                    </div>
                                )}
                                {result.results?.some(r => r.status !== 'removed') && (
                                    <div style={{ fontSize: 11.5, fontFamily: 'monospace', opacity: 0.75, marginBottom: 10 }}>
                                        {result.results.filter(r => r.status !== 'removed').map(r =>
                                            <div key={r.machine_id}>machine {r.machine_id}: {r.status} {r.error || r.container_error || ''}</div>)}
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button onClick={closeModal} style={{
                                        fontSize: 13, fontWeight: 600, padding: '8px 18px', borderRadius: 8,
                                        border: 'none', background: 'var(--accent, #3b82f6)', color: '#fff', cursor: 'pointer'
                                    }}>Close</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================
// VMs TAB
// ============================================================

function VMsTab({ client, machines, servers }) {
    const serversForClient = useMemo(() => {
        const ids = new Set(machines.map(m => m.server_id).filter(Boolean))
        return servers.filter(s => ids.has(s.id))
    }, [machines, servers])

    const [lxcByServer, setLxcByServer] = useState({})
    const [loading, setLoading] = useState(false)
    const [err, setErr] = useState(null)

    async function load() {
        if (serversForClient.length === 0) return
        setLoading(true); setErr(null)
        try {
            const results = {}
            await Promise.all(serversForClient.map(async s => {
                try {
                    const r = await audiosocketBotsApi.serverLxcs(s.id)
                    if (r.data.success) results[s.id] = r.data.lxcs || []
                    else results[s.id] = { _err: r.data.error || 'failed' }
                } catch (e) {
                    results[s.id] = { _err: e.response?.data?.error || e.message }
                }
            }))
            setLxcByServer(results)
        } catch (e) {
            setErr(e.message)
        } finally { setLoading(false) }
    }
    useEffect(() => { load() /* eslint-disable-next-line */ }, [serversForClient.map(s => s.id).join(',')])

    return (
        <div className="dialer-info-tab">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.7 }}>All LXCs on servers hosting this client's bots</div>
                <button className="btn btn-icon btn-sm" onClick={load} disabled={loading}>
                    <RefreshCw size={14} className={loading ? 'spin' : ''} />
                </button>
            </div>
            {err && <div style={{ padding: 8, fontSize: 12, color: '#ef4444' }}>{err}</div>}
            {serversForClient.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', opacity: 0.6, background: 'var(--bg-card)', borderRadius: 10 }}>
                    No servers for this client (no bots yet).
                </div>
            ) : serversForClient.map(s => {
                const lxcs = lxcByServer[s.id]
                const isErr = lxcs && lxcs._err
                return (
                    <div key={s.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10, marginBottom: 14, overflow: 'hidden' }}>
                        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Server size={14} />
                            <strong>{s.name}</strong>
                            <span style={{ fontSize: 12, fontFamily: 'monospace', opacity: 0.7 }}>({s.host_ip})</span>
                            {Array.isArray(lxcs) && <span style={{ fontSize: 11, opacity: 0.65, marginLeft: 'auto' }}>{lxcs.length} LXCs</span>}
                        </div>
                        {isErr ? (
                            <div style={{ padding: 16, fontSize: 12, color: '#ef4444' }}>{lxcs._err}</div>
                        ) : !Array.isArray(lxcs) ? (
                            <div style={{ padding: 16, fontSize: 12, opacity: 0.6 }}>loading…</div>
                        ) : (
                            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', opacity: 0.7 }}>
                                        <th style={{ padding: '8px 12px' }}>LXC</th>
                                        <th style={{ padding: '8px 12px' }}>Name</th>
                                        <th style={{ padding: '8px 12px' }}>IP</th>
                                        <th style={{ padding: '8px 12px' }}>Status</th>
                                        <th style={{ padding: '8px 12px' }}>Kind</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lxcs.sort((a, b) => a.id - b.id).map(l => {
                                        const isBot = machines.find(m => parseInt(m.lxc_id) === l.id)
                                        const isAsterisk = parseInt(s.asterisk_lxc_id) === l.id
                                        const kind = isBot ? 'bot' : isAsterisk ? 'asterisk' : 'external'
                                        const color = isBot ? '#22c55e' : isAsterisk ? '#3b82f6' : '#94a3b8'
                                        return (
                                            <tr key={l.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{l.id}</td>
                                                <td style={{ padding: '6px 12px' }}>{l.name || '—'}</td>
                                                <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{l.ip || '—'}</td>
                                                <td style={{ padding: '6px 12px' }}>
                                                    <span style={{
                                                        fontSize: 11, padding: '2px 6px', borderRadius: 10,
                                                        background: l.status === 'running' ? 'rgba(34,197,94,0.15)' : 'rgba(156,163,175,0.15)',
                                                        color: l.status === 'running' ? '#22c55e' : '#9ca3af'
                                                    }}>
                                                        {l.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '6px 12px' }}>
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                                                        padding: '2px 6px', borderRadius: 4,
                                                        color, border: `1px solid ${color}40`, background: `${color}15`
                                                    }}>
                                                        {kind}
                                                    </span>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// ============================================================
// CALL LOGS TAB
// ============================================================

const REFRESH_SECONDS = 10   // Call Logs auto-refresh interval

function CallLogsTab({ client, machines, range, scopeParams, initialSearch = '' }) {
    const [calls, setCalls] = useState([])
    const [total, setTotal] = useState(0)
    const [dispoCounts, setDispoCounts] = useState({})
    const [loading, setLoading] = useState(false)
    const [err, setErr] = useState(null)
    const [page, setPage] = useState(1)
    const [perPage] = useState(100)
    const [search, setSearch] = useState(initialSearch)
    const [sipFilter, setSipFilter] = useState('')
    const [dispoFilter, setDispoFilter] = useState('')
    const [selectedCall, setSelectedCall] = useState(null)
    const scopeKey = JSON.stringify(scopeParams)

    const [autoRefresh, setAutoRefresh] = useState(true)   // poll for new calls every 10s
    const [nextIn, setNextIn] = useState(REFRESH_SECONDS)  // countdown shown in the refresh button
    const [spinKey, setSpinKey] = useState(0)              // bump → the icon spins once
    const nextInRef = useRef(REFRESH_SECONDS)
    const inFlight = useRef(false)

    const [searchDebounced, setSearchDebounced] = useState(initialSearch)
    const searchTimer = useRef(null)
    useEffect(() => {
        if (searchTimer.current) clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(() => setSearchDebounced(search), 300)
        return () => searchTimer.current && clearTimeout(searchTimer.current)
    }, [search])

    // silent = background poll: no spinner, and a failed poll keeps the current
    // rows on screen instead of replacing them with an error.
    async function load(silent = false) {
        if (silent && inFlight.current) return
        inFlight.current = true
        setSpinKey(k => k + 1)
        nextInRef.current = REFRESH_SECONDS
        setNextIn(REFRESH_SECONDS)
        if (!silent) { setLoading(true); setErr(null) }
        try {
            const params = { page, per_page: perPage, ...scopeParams }
            if (range.from) params.from = range.from
            if (range.to) params.to = range.to
            if (dispoFilter) params.disposition = dispoFilter
            if (sipFilter) params.sip_user = sipFilter
            if (searchDebounced) params.search = searchDebounced

            const r = await audiosocketBotsApi.listCallLogs(params)
            const d = r.data
            if (!d.success) throw new Error(d.error || 'failed')
            setCalls(d.calls || [])
            setTotal(d.total || 0)
            setDispoCounts(d.disposition_counts || {})
            if (silent) setErr(null)
        } catch (e) {
            if (!silent) setErr(e.response?.data?.error || e.message)
        } finally {
            inFlight.current = false
            if (!silent) setLoading(false)
        }
    }
    useEffect(() => { load() /* eslint-disable-next-line */ }, [scopeKey, page, range.from, range.to, dispoFilter, sipFilter, searchDebounced])

    // Poll with the current filters/page: a 1s tick counts nextIn down and
    // refreshes at 0 (load() resets it, so a manual refresh restarts the count).
    // The ref always points at the latest load(), so the tick never fetches with
    // stale filters. While the browser tab is hidden it holds at 0, then
    // refreshes as soon as the tab is visible again.
    const loadRef = useRef(load)
    loadRef.current = load
    useEffect(() => {
        if (!autoRefresh) return
        nextInRef.current = REFRESH_SECONDS
        setNextIn(REFRESH_SECONDS)
        const id = setInterval(() => {
            if (nextInRef.current > 1) {
                nextInRef.current -= 1
                setNextIn(nextInRef.current)
            } else if (document.visibilityState === 'visible') {
                loadRef.current(true)          // resets the countdown itself
            } else {
                nextInRef.current = 0
                setNextIn(0)
            }
        }, 1000)
        return () => clearInterval(id)
    }, [autoRefresh])
    useEffect(() => { setPage(1) }, [scopeKey, range.from, range.to, dispoFilter, sipFilter, searchDebounced])
    // A SIP picked here that the page filter no longer includes → back to all.
    useEffect(() => { if (sipFilter && !machines.some(m => m.sip_user === sipFilter)) setSipFilter('') }, [machines, sipFilter])

    const pageCount = Math.max(1, Math.ceil(total / perPage))
    const sipsForClient = useMemo(() => machines.map(m => m.sip_user).filter(Boolean).sort(), [machines])

    return (
        <div className="dcl-container" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
            <div className="dcl-toolbar" style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                {/* .dcl-search is the pill (border, padding, icon spacing); the input inside is borderless */}
                <div className={`dcl-search tw-min-w-[240px] ${search ? 'active' : ''}`}>
                    <Search size={14} />
                    <input
                        type="text"
                        placeholder="Search phone or transcript..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <button onClick={() => setSearch('')} title="Clear search"
                            className="tw-inline-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-muted hover:tw-text-fg">
                            <X size={14} />
                        </button>
                    )}
                </div>
                <select
                    value={sipFilter}
                    onChange={e => setSipFilter(e.target.value)}
                    className="dcl-select"
                    style={{ minWidth: 160 }}>
                    <option value="">All SIPs ({sipsForClient.length})</option>
                    {sipsForClient.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <label title="Fetch new calls every 10 seconds"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                    Live
                </label>
                <button className="btn btn-icon btn-sm" onClick={() => load()} disabled={loading}
                    title={autoRefresh ? `Refreshes in ${nextIn}s — click to refresh now` : 'Refresh now'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: autoRefresh ? 48 : undefined, justifyContent: 'center' }}>
                    {/* key change remounts the icon → the one-turn animation replays */}
                    <RefreshCw key={spinKey} size={14} className={spinKey ? 'spin-once' : ''} />
                    {autoRefresh && (
                        <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', minWidth: 14, textAlign: 'right', opacity: 0.8 }}>
                            {nextIn}
                        </span>
                    )}
                </button>
            </div>

            <div className="dcl-dispo-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {DISPOSITIONS.map(d => {
                    const cnt = dispoCounts[d.key] || 0
                    if (cnt === 0) return null
                    const selected = dispoFilter === d.key
                    return (
                        <button key={d.key}
                            className={`dcl-chip ${selected ? 'selected' : ''}`}
                            onClick={() => setDispoFilter(selected ? '' : d.key)}
                            style={{
                                padding: '4px 10px', fontSize: 12, fontWeight: 600,
                                borderRadius: 16, cursor: 'pointer',
                                border: `1px solid ${selected ? d.color : 'transparent'}`,
                                background: selected ? `${d.color}25` : 'var(--bg-card)',
                                color: d.color,
                                display: 'inline-flex', alignItems: 'center', gap: 6
                            }}>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{cnt}</span>
                            <span style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{d.label}</span>
                        </button>
                    )
                })}
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11, opacity: 0.7, alignSelf: 'center' }}>
                    {total === 0 ? 'No calls' : `${(page - 1) * perPage + 1}-${Math.min(page * perPage, total)} of ${total}`}
                </span>
            </div>

            {err && <div style={{ padding: 12, color: '#ef4444', fontSize: 12 }}>{err}</div>}

            <div className="dcl-table-wrapper" style={{ flex: 1, overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10 }}>
                {loading && calls.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', opacity: 0.6 }}>loading…</div>
                ) : calls.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', opacity: 0.6 }}>No calls match your filters.</div>
                ) : (
                    <table className="dcl-table" style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
                            <tr style={{ textAlign: 'left', opacity: 0.7, borderBottom: '1px solid var(--border-color)' }}>
                                <th style={{ padding: '10px 12px' }}>TIME</th>
                                <th style={{ padding: '10px 12px' }}>PHONE</th>
                                <th style={{ padding: '10px 12px' }}>DISPOSITION</th>
                                <th style={{ padding: '10px 12px' }}>DURATION</th>
                                <th style={{ padding: '10px 12px' }}>RECORDING</th>
                                <th style={{ padding: '10px 12px' }}>HUNG UP</th>
                                <th style={{ padding: '10px 12px' }}>VOICE SET</th>
                                <th style={{ padding: '10px 12px' }}>STEP</th>
                                <th style={{ padding: '10px 12px' }}>SIP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {calls.map(c => {
                                const dispoKey = c.disposition_key || c.disposition
                                const dispoColor = DISPO_COLOR(dispoKey)
                                const dispoLabel = DISPO_LABEL(dispoKey)
                                const inProgress = dispoKey === 'in-progress'
                                return (
                                    <tr key={c.id}
                                        onClick={() => setSelectedCall(c)}
                                        style={{ borderTop: '1px solid var(--border-color)', cursor: 'pointer' }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtDateTime(c.started_at)}</td>
                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{c.phone_number || '—'}</td>
                                        <td style={{ padding: '8px 12px' }}>
                                            <span style={{ color: dispoColor, fontWeight: 500 }}
                                                title={inProgress ? 'Call is still running — its result arrives when it ends'
                                                    : dispoKey === 'unknown' ? 'No end report from the bot — it never handled the call, or the report was lost' : undefined}>
                                                {inProgress
                                                    ? <span className="dcl-live-dot" />
                                                    : <PhoneIncoming size={12} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />}
                                                {dispoLabel}
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px 12px' }}>{inProgress ? '—' : fmtDuration(c.duration_seconds)}</td>
                                        <td
                                            style={{ padding: '8px 12px' }}
                                            onClick={(e) => e.stopPropagation()}
                                            onDoubleClick={() => setSelectedCall(c)}
                                            title="Click to play · Double-click for details"
                                        >
                                            <InlineRecordingPlayer
                                                machineId={c.machine_id}
                                                filename={c.recording_filename}
                                                durationSeconds={c.duration_seconds}
                                            />
                                        </td>
                                        <td style={{ padding: '8px 12px' }}>
                                            {c.hung_up_by ? (() => {
                                                const huColor =
                                                    c.hung_up_by === 'bot' ? '#3b82f6' :
                                                        c.hung_up_by === 'caller' ? '#f472b6' :
                                                            c.hung_up_by === 'transferred' ? '#22c55e' :
                                                                '#9ca3af'
                                                const huLabel =
                                                    c.hung_up_by === 'bot' ? 'Bot' :
                                                        c.hung_up_by === 'caller' ? 'Caller' :
                                                            c.hung_up_by === 'transferred' ? 'Transferred' :
                                                                c.hung_up_by
                                                return (
                                                    <span style={{
                                                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                                                        background: `${huColor}26`, color: huColor
                                                    }}>{huLabel}</span>
                                                )
                                            })() : '—'}
                                        </td>
                                        <td style={{ padding: '8px 12px' }}>{c.voice_set_name || '—'}</td>
                                        <td style={{ padding: '8px 12px' }}>{c.steps_count || 0}</td>
                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{c.sip_user || '—'}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {pageCount > 1 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '10px 4px', fontSize: 12 }}>
                    <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(1)}>First</button>
                    <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        <ChevronLeft size={14} />
                    </button>
                    <span style={{ minWidth: 60, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                        {page} / {pageCount}
                    </span>
                    <button className="btn btn-secondary btn-sm" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>
                        <ChevronRight size={14} />
                    </button>
                    <button className="btn btn-secondary btn-sm" disabled={page >= pageCount} onClick={() => setPage(pageCount)}>Last</button>
                </div>
            )}

            {selectedCall && (
                <TranscriptModal
                    callId={selectedCall.id}
                    onClose={() => setSelectedCall(null)}
                />
            )}
        </div>
    )
}

// ============================================================
// TRANSCRIPT MODAL
// ============================================================

function TranscriptModal({ callId, onClose }) {
    const [call, setCall] = useState(null)
    const [loading, setLoading] = useState(true)
    const [err, setErr] = useState(null)

    useEffect(() => {
        let cancelled = false
        audiosocketBotsApi.getCallLog(callId)
            .then(r => { if (!cancelled) setCall(r.data.call) })
            .catch(e => { if (!cancelled) setErr(e.response?.data?.error || e.message) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [callId])

    const audioUrl = call?.machine_id && call?.recording_filename
        ? audiosocketBotsApi.machineRecordingUrl(call.machine_id, call.recording_filename)
        : null

    const dispoColor = call ? DISPO_COLOR(call.disposition_key || call.disposition) : '#94a3b8'
    const dispoLabel = call ? DISPO_LABEL(call.disposition_key || call.disposition) : '—'

    return (
        <div className="modal-overlay" onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="modal" onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--bg-card)', borderRadius: 12, width: '90%', maxWidth: 700,
                    maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    border: '1px solid var(--border-color)'
                }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
                        <PhoneCall size={16} /> Call Transcript
                    </h3>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                        <X size={18} />
                    </button>
                </div>

                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', opacity: 0.6 }}>loading…</div>
                ) : err ? (
                    <div style={{ padding: 20, color: '#ef4444' }}>{err}</div>
                ) : call ? (
                    <>
                        <div style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border-color)' }}>
                            <MetaCell label="PHONE" value={call.phone_number || '—'} mono />
                            <MetaCell label="DURATION" value={fmtDuration(call.duration_seconds)} />
                            <MetaCell label="STEPS" value={call.steps_count || 0} />
                            <MetaCell label="HUNG UP"
                                value={
                                    call.hung_up_by === 'bot' ? 'Bot' :
                                        call.hung_up_by === 'caller' ? 'Caller' :
                                            call.hung_up_by === 'transferred' ? 'Transferred' :
                                                (call.hung_up_by || '—')
                                }
                                color={
                                    call.hung_up_by === 'bot' ? '#3b82f6' :
                                        call.hung_up_by === 'caller' ? '#f472b6' :
                                            call.hung_up_by === 'transferred' ? '#22c55e' :
                                                '#9ca3af'
                                } />
                            <MetaCell label="RESULT" value={dispoLabel} color={dispoColor} />
                        </div>

                        {audioUrl && (
                            <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-color)', background: 'rgba(0,0,0,0.2)' }}>
                                <audio controls preload="none" src={audioUrl} style={{ width: '100%', height: 36 }} />
                                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontFamily: 'monospace' }}>{call.recording_filename}</span>
                                    <a href={audioUrl} download style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'inherit', textDecoration: 'none' }}>
                                        <Download size={12} /> Download
                                    </a>
                                </div>
                            </div>
                        )}

                        <div style={{ flex: 1, overflow: 'auto', padding: '12px 18px' }}>
                            {(!call.events || call.events.length === 0) ? (
                                <div style={{ padding: 20, textAlign: 'center', opacity: 0.6 }}>
                                    No timeline events recorded for this call.
                                </div>
                            ) : (
                                call.events.map((e, i) => <EventRow key={i} event={e} />)
                            )}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    )
}

function MetaCell({ label, value, color, mono }) {
    return (
        <div>
            <div style={{ fontSize: 10, opacity: 0.6, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ marginTop: 2, fontSize: 14, fontWeight: 600, color: color || 'inherit', fontFamily: mono ? 'monospace' : undefined }}>
                {value}
            </div>
        </div>
    )
}

function EventRow({ event }) {
    const role = event.role
    const t = `${Math.floor(event.t / 60)}:${String(Math.floor(event.t % 60)).padStart(2, '0')}`
    const isMatch = role === 'match'
    const isMidPitch = role === 'caller' && event.mid          // caller spoke while the bot was still playing audio
    const isStopPitch = !!event.stopped_pitch                   // bot cut its own audio (interrupt)

    let roleColor, roleLabel, RoleIcon
    if (isStopPitch) { roleColor = '#f97316'; roleLabel = 'Bot Stopped Pitch'; RoleIcon = Square }
    else if (role === 'caller') { roleColor = '#22c55e'; roleLabel = 'Caller'; RoleIcon = Users }
    else if (role === 'bot') { roleColor = '#dc2626'; roleLabel = 'Bot'; RoleIcon = Headphones }
    else if (role === 'match') { roleColor = '#f59e0b'; roleLabel = 'Keyword Match'; RoleIcon = Tag }
    else { roleColor = '#9ca3af'; roleLabel = 'System'; RoleIcon = Activity }

    const bg = isMatch ? 'rgba(245, 158, 11, 0.07)'
        : isStopPitch ? 'rgba(249, 115, 22, 0.10)'
            : isMidPitch ? 'rgba(245, 158, 11, 0.05)'
                : 'transparent'
    return (
        <div style={{
            display: 'flex', gap: 12, padding: '10px 0',
            borderBottom: '1px solid var(--border-color)',
            background: bg
        }}>
            <div style={{ width: 44, fontSize: 11, fontFamily: 'monospace', opacity: 0.7, paddingTop: 2 }}>{t}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: roleColor, fontWeight: 600, fontSize: 12 }}>
                        <RoleIcon size={12} /> {roleLabel}
                    </span>
                    <span style={{ fontSize: 10, opacity: 0.55 }}>Step {event.step}</span>
                    {isMidPitch && (
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.3, color: '#f59e0b',
                            background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.45)',
                            borderRadius: 4, padding: '1px 5px'
                        }}>
                            MID-PITCH
                        </span>
                    )}
                </div>
                <div style={{
                    fontSize: 13,
                    fontStyle: (role === 'bot' || isStopPitch) ? 'italic' : 'normal',
                    fontWeight: (isMatch || isStopPitch) ? 600 : 400,
                    color: isMatch ? '#f59e0b' : isStopPitch ? '#f97316' : 'inherit',
                    wordBreak: 'break-word'
                }}>
                    {event.text}
                </div>
            </div>
        </div>
    )
}

// ============================================================
// SCOPE FILTERS — narrow every tab to some SIPs / servers / templates
// ============================================================

function ScopeFilters({ machines, servers, filters, onChange, shown }) {
    const sipOptions = useMemo(() => machines.filter(m => m.sip_user)
        .map(m => ({ value: String(m.sip_user), label: String(m.sip_user), sub: [m.template_name, m.server_name].filter(Boolean).join(' · ') }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })), [machines])
    const serverOptions = useMemo(() => {
        const count = new Map()
        for (const m of machines) if (m.server_id) count.set(m.server_id, (count.get(m.server_id) || 0) + 1)
        return [...count].map(([id, n]) => {
            const s = servers.find(x => x.id === id)
            return { value: String(id), label: s?.name || `Server #${id}`, sub: `${n} bot${n === 1 ? '' : 's'}${s?.host_ip ? ` · ${s.host_ip}` : ''}` }
        }).sort((a, b) => a.label.localeCompare(b.label))
    }, [machines, servers])
    const templateOptions = useMemo(() => {
        const t = new Map()
        for (const m of machines) if (m.template_id) {
            const cur = t.get(m.template_id) || { value: String(m.template_id), label: m.template_name || `Template #${m.template_id}`, n: 0 }
            cur.n++; t.set(m.template_id, cur)
        }
        return [...t.values()].map(({ n, ...o }) => ({ ...o, sub: `${n} bot${n === 1 ? '' : 's'}` })).sort((a, b) => a.label.localeCompare(b.label))
    }, [machines])

    const any = filters.sips.length || filters.servers.length || filters.templates.length
    const set = (k) => (vals) => onChange({ ...filters, [k]: vals })
    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-[10px] tw-pb-1 tw-pt-3">
            <MultiPicker label="SIPs" icon={Cpu} options={sipOptions} selected={filters.sips} onChange={set('sips')} groupInput allLabel="All" />
            <MultiPicker label="Servers" icon={Server} options={serverOptions} selected={filters.servers} onChange={set('servers')} allLabel="All" />
            <MultiPicker label="Templates" icon={FileText} options={templateOptions} selected={filters.templates} onChange={set('templates')} allLabel="All" />
            {any ? (
                <>
                    <span className="tw-text-xs tw-text-muted">{shown} of {machines.length} bots</span>
                    <button className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-text-sky-400 hover:tw-underline"
                        onClick={() => onChange(NO_FILTERS)}>Clear filters</button>
                </>
            ) : null}
        </div>
    )
}
